import React, { useState, useEffect, useRef } from 'react';
import { FileText, Download, Calendar, Users, RefreshCw, Filter, ChevronDown, AlertTriangle, CheckCircle, Clock, Check, X } from 'lucide-react';
import { toast } from '../ui/simple-toaster';
import { api, apiBlob } from '../../utils/api';

const REPORT_CONFIGS = {
  daily_compliance: {
    name: 'Daily Compliance',
    description: 'LAW and Business Rules status per driver',
    icon: CheckCircle,
    color: 'emerald'
  },
  driver_weekly_hours: {
    name: 'Weekly Hours (72h/7d)',
    description: 'Total work hours vs 72h limit',
    icon: Clock,
    color: 'blue'
  },
  fortnightly_hours: {
    name: 'Fortnightly Hours (144h/14d)',
    description: '14-day rolling compliance',
    icon: Clock,
    color: 'purple'
  },
  law_breaches: {
    name: 'LAW Breaches',
    description: 'HVN Law violations (FRHV only) - legal non-compliance',
    icon: AlertTriangle,
    color: 'red'
  },
  business_non_conformances: {
    name: 'Business Non-Conformances',
    description: 'Business rule violations (non-LAW) - policy breaches',
    icon: AlertTriangle,
    color: 'amber'
  },
  continuous_work_breach: {
    name: 'Continuous Work Breaches',
    description: '5h15 threshold breaches',
    icon: AlertTriangle,
    color: 'red'
  },
  night_rest_compliance: {
    name: 'Night Rest Compliance',
    description: '7h/24h night rest verification',
    icon: CheckCircle,
    color: 'indigo'
  },
  diary_mode_transition: {
    name: 'Diary Mode Transitions',
    description: 'HV ↔ FRHV switches with REST credits',
    icon: RefreshCw,
    color: 'amber'
  },
  provisional_rollout: {
    name: 'Provisional Status',
    description: 'Days to FULL_ENFORCE by driver',
    icon: Calendar,
    color: 'cyan'
  }
};

const DriverMultiSelect = ({ drivers, selectedIds, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = (id) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);
  };

  const selectAll = () => onChange(drivers.map(d => d.driver_id));
  const clearAll = () => onChange([]);

  const label = selectedIds.length === 0
    ? 'All drivers'
    : selectedIds.length === 1
      ? drivers.find(d => d.driver_id === selectedIds[0])?.users
        ? `${drivers.find(d => d.driver_id === selectedIds[0]).users.first_name} ${drivers.find(d => d.driver_id === selectedIds[0]).users.last_name}`
        : '1 driver'
      : `${selectedIds.length} drivers selected`;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-left flex items-center justify-between"
        data-testid="filter-drivers"
      >
        <span className="truncate">{label}</span>
        <ChevronDown className={`w-4 h-4 ml-2 transition flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700">
            <button onClick={selectAll} className="text-xs text-emerald-400 hover:text-emerald-300">Select All</button>
            <button onClick={clearAll} className="text-xs text-slate-400 hover:text-slate-300">Clear</button>
          </div>
          {drivers.map(d => {
            const name = `${d.users?.first_name || ''} ${d.users?.last_name || ''}`.trim();
            const checked = selectedIds.includes(d.driver_id);
            return (
              <div
                key={d.driver_id}
                onClick={() => toggle(d.driver_id)}
                className="flex items-center gap-3 px-3 py-2 hover:bg-slate-700/50 cursor-pointer"
                data-testid={`driver-option-${d.driver_id}`}
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                  checked ? 'bg-emerald-500 border-emerald-500' : 'border-slate-500'
                }`}>
                  {checked && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="text-white text-sm truncate">{name || 'Unknown'}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ReportsPanel = ({ drivers = [] }) => {
  const [availableReports, setAvailableReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    driverIds: []
  });
  const [showFilters, setShowFilters] = useState(true); // Open by default

  useEffect(() => {
    loadAvailableReports();
  }, []);

  const loadAvailableReports = async () => {
    try {
      const result = await api('/reports');
      setAvailableReports(result.reports || []);
    } catch (err) {
      console.error('Failed to load reports:', err);
    }
  };

  const generateReport = async (reportType, format = 'json') => {
    setLoading(true);
    try {
      const body = {
        report_type: reportType,
        start_date: new Date(filters.startDate).toISOString(),
        end_date: new Date(filters.endDate).toISOString(),
        driver_ids: filters.driverIds.length > 0 ? filters.driverIds : null,
        format
      };

      if (format === 'csv') {
        const blob = await apiBlob('/reports/generate', {
          method: 'POST',
          body: JSON.stringify(body)
        });
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportType}_${filters.startDate}_${filters.endDate}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('CSV downloaded');
      } else {
        const result = await api('/reports/generate', {
          method: 'POST',
          body: JSON.stringify(body)
        });
        setReportData(result);
        setSelectedReport(reportType);
      }
    } catch (err) {
      toast.error('Failed to generate report: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      COMPLIANT: 'bg-emerald-500/20 text-emerald-400',
      BREACH: 'bg-red-500/20 text-red-400',
      LAW_BREACH: 'bg-red-500/20 text-red-400',
      WARNING: 'bg-amber-500/20 text-amber-400',
      SHADOW_WARNING: 'bg-amber-500/20 text-amber-400',
      NON_COMPLIANT: 'bg-red-500/20 text-red-400',
      FULL_ENFORCE: 'bg-emerald-500/20 text-emerald-400',
      SOFT_ENFORCE: 'bg-amber-500/20 text-amber-400',
      WARNING_ONLY: 'bg-blue-500/20 text-blue-400',
      INACTIVE: 'bg-slate-500/20 text-slate-400'
    };
    return statusStyles[status] || 'bg-slate-500/20 text-slate-400';
  };

  const renderReportData = () => {
    if (!reportData || !reportData.data) return null;

    const data = reportData.data;
    if (data.length === 0) {
      return (
        <div className="text-center py-12 text-slate-400">
          No data found for the selected criteria
        </div>
      );
    }

    // Get column headers from first row
    const columns = Object.keys(data[0]).filter(k => 
      !['driver_id', 'diary_id', 'crossing_id', 'transition_id'].includes(k)
    );

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-400 border-b border-slate-700">
              {columns.map(col => (
                <th key={col} className="pb-3 pr-4 font-medium whitespace-nowrap">
                  {col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-white">
            {data.map((row, idx) => (
              <tr key={row.driver_id || row.id || `row-${idx}`} className="border-b border-slate-700/50">
                {columns.map(col => (
                  <td key={col} className="py-3 pr-4 whitespace-nowrap">
                    {col === 'status' || col === 'compliance_status' || col === 'current_mode' ? (
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(row[col])}`}>
                        {row[col]}
                      </span>
                    ) : col.includes('hours') ? (
                      <span>{row[col]}h</span>
                    ) : col.includes('percentage') ? (
                      <span>{row[col]}%</span>
                    ) : col.includes('timestamp') || col.includes('date') ? (
                      <span className="text-slate-400">
                        {row[col] ? new Date(row[col]).toLocaleString() : '-'}
                      </span>
                    ) : (
                      <span>{row[col] ?? '-'}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Filters Bar */}
      <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-white font-medium"
          data-testid="toggle-filters-btn"
        >
          <Filter className="w-5 h-5" />
          Filters
          <ChevronDown className={`w-4 h-4 transition ${showFilters ? 'rotate-180' : ''}`} />
        </button>

        {showFilters && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                data-testid="filter-start-date"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                data-testid="filter-end-date"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Drivers</label>
              <DriverMultiSelect
                drivers={drivers}
                selectedIds={filters.driverIds}
                onChange={(ids) => setFilters(prev => ({ ...prev, driverIds: ids }))}
              />
            </div>
          </div>
        )}
      </div>

      {/* Report Selection */}
      {!selectedReport && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(REPORT_CONFIGS).map(([type, config]) => {
            const Icon = config.icon;
            return (
              <div
                key={type}
                className="bg-slate-800/50 rounded-2xl border border-slate-700 p-5 hover:border-emerald-500/50 transition cursor-pointer"
                onClick={() => generateReport(type)}
                data-testid={`report-card-${type}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl bg-${config.color}-500/20 flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-6 h-6 text-${config.color}-400`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold">{config.name}</h3>
                    <p className="text-slate-400 text-sm mt-1">{config.description}</p>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); generateReport(type); }}
                    className="flex-1 px-3 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 text-sm flex items-center justify-center gap-1"
                    disabled={loading}
                  >
                    <FileText className="w-4 h-4" />
                    View
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); generateReport(type, 'csv'); }}
                    className="flex-1 px-3 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm flex items-center justify-center gap-1"
                    disabled={loading}
                  >
                    <Download className="w-4 h-4" />
                    CSV
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Report Results */}
      {selectedReport && reportData && (
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-white">
                {REPORT_CONFIGS[selectedReport]?.name || selectedReport}
              </h2>
              <p className="text-slate-400 text-sm">
                {reportData.row_count} records • Generated {new Date(reportData.generated_at).toLocaleString()}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => generateReport(selectedReport, 'csv')}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 flex items-center gap-2"
                data-testid="download-csv-btn"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              <button
                onClick={() => { setSelectedReport(null); setReportData(null); }}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
                data-testid="back-to-reports-btn"
              >
                Back
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
            </div>
          ) : (
            renderReportData()
          )}
        </div>
      )}

      {loading && !selectedReport && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
        </div>
      )}
    </div>
  );
};

export default ReportsPanel;
